/* eslint-disable no-console */
import { useCallback, useMemo } from 'react';

import { useLingui } from '@lingui/react/macro';
import _ from 'lodash';

import type { TDisplayOrderQuadrantAbbreviations } from '../../modules/patientList/patientStrip/patientStripOdontogram/types';
import { useGetLocalization } from '../../react-query/useGetLocalization';
import { usePreferences } from '../../react-query/usePreferences';

export type TToothToQuad = {
  toothId: string;
  quadrant: string;
};

export const useUniversalToFDI = () => {
  const { data: prefs } = usePreferences();
  const { data } = useGetLocalization();
  const { toothLocalization } = prefs || {};
  const { toothMapping, quadMapping } = data || {};
  const { t } = useLingui();

  const isFDI = useMemo(() => toothLocalization === 'fdi', [toothLocalization]);

  const displayToothNumber = useCallback(
    (toothId?: number | string) => {
      if (!isFDI && toothId) {
        return _.upperFirst(String(toothId).toLowerCase());
      }
      if (!isFDI) {
        return t`Unknown`;
      }

      const localizedTooth = (toothMapping ?? []).find(
        (item) =>
          item.universal.toLowerCase() === String(toothId).toLowerCase(),
      );
      return localizedTooth?.fdi ?? t`Unknown`;
    },
    [isFDI, toothMapping, t],
  );

  const displayQuadrantAlphaToNumeric = useCallback(
    (quadrant?: string) => {
      const localizedQuad = (quadMapping ?? []).find(
        (item) => item.quadAlpha === quadrant,
      );

      return localizedQuad?.quadNum ?? '';
    },
    [quadMapping],
  );

  const displayQuadrantNumericToAlpha = useCallback(
    (quadrant?: string) => {
      const localizedQuad = (quadMapping ?? []).find(
        (item) => item.quadNum === quadrant,
      );

      return localizedQuad?.quadAlpha ?? '';
    },
    [quadMapping],
  );

  const displayLocalizedQuadrantShortName = useCallback(
    (quadrant: TDisplayOrderQuadrantAbbreviations): string => {
      const localizedQuad = quadMapping?.find(
        (item) => item.quadAlpha === quadrant,
      );

      if (isFDI) {
        return `Q${localizedQuad?.quadNum ?? ''}`;
      }

      return localizedQuad?.quadAlpha ?? '';
    },
    [quadMapping, isFDI],
  );

  const displayLocalizedQuadrantLongName = useCallback(
    (quadrant: TDisplayOrderQuadrantAbbreviations) => {
      const localizedQuad = quadMapping?.find(
        (item) => item.quadAlpha === quadrant,
      );

      if (isFDI) {
        return t`Quadrant ` + (localizedQuad?.quadNum || '');
      } else if (localizedQuad?.quadAlpha === 'UL') {
        return t`Upper Left`;
      } else if (localizedQuad?.quadAlpha === 'LL') {
        return t`Lower Left`;
      } else if (localizedQuad?.quadAlpha === 'UR') {
        return t`Upper Right`;
      } else if (localizedQuad?.quadAlpha === 'LR') {
        return t`Lower Right`;
      }

      return localizedQuad?.quadAlpha ?? '';
    },
    [quadMapping, isFDI, t],
  );

  const getTeethForQuadrant = useCallback(
    (quadrant: string, numericOnly = true) => {
      return toothMapping
        ?.filter((tl) => tl.quadAlpha === quadrant)
        .filter((q) => numericOnly && !isNaN(Number(q.universal)))
        .map((q) => q.universal);
    },
    [toothMapping],
  );

  const toothToQuadrant = useMemo(() => {
    return toothMapping?.map((v) => ({
      toothId: v.universal,
      quadrant: v.quadAlpha as string,
    })) as TToothToQuad[] | undefined;
  }, [toothMapping]);

  return {
    isFDI,
    displayToothNumber,
    displayQuadrantAlphaToNumeric,
    displayQuadrantNumericToAlpha,
    getTeethForQuadrant,
    toothToQuadrant,
    displayLocalizedQuadrantShortName,
    displayLocalizedQuadrantLongName,
  };
};
